import classname from "classnames";
import i18next from "i18next";
import React, { PureComponent, ReactNode, RefObject, Suspense } from "react";
import { State } from "react-burger-menu";
import ReactDOM from "react-dom";
import { RouteComponentProps } from "react-router-dom";
import { Sticky } from "react-sticky";
import { needReload } from "src/components/Hyperlink/Hyperlink.component";
import withRenderOn from "src/hoc/withRenderOn";
import { IRenderOn } from "src/hooks/useRenderOn";
import { IHeaderPayload, ISetHeader } from "src/redux/header/actions";
import { IHeaderState } from "src/redux/header/reducer";
import styled from "styled-components";

import { Hyperlink, LangPicker } from "../../../..";
import logoWebsite from "../../../../../assets/images/logoWebsite.svg";
import logoWebsiteDesktop from "../../../../../assets/images/logoWebsiteDesktop.png";
import { IWithCart } from "../../../../../hoc/repository/withCart/withCart";
import { translate } from "../../../../../services/translation.service";
import theme, { media } from "../../../../../theme";
import { ICart, ICartProductDetail } from "../../../../../types/cart";
import Icon from "../../../../Icon";
import { IProps as IProductsCount } from "../../../../Icon/Icon.component";
import InstantSearch from "../../../../InstantSearch";
import { OrderRecapBody } from "../../../components/OrderRecap";
import AppMenu from "../AppMenu";
import MiniLogin from "../AppMenu/components/AppContentMenuHeader";
import AppContentMenuList from "../AppMenu/components/AppContentMenuList";

import MiniCart from "./components/MiniCart";

const Wrapper = styled.div`
  display: flex;
`;

const HeaderWrapper = styled.header<Pick<IState, "isSearchOpen">>`
  position: relative;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  ${({ isSearchOpen }: Pick<IState, "isSearchOpen">): string =>
    isSearchOpen ? "z-index: 11;" : ""}

  ${media.laptop} {
    z-index: 10;

    &.instantSearch,
    &.loginOpen,
    &.recapOpen {
      z-index: 30;
    }

    .mini-login,
    .mini-cart {
      & > .disabled {
        opacity: 0.3;
        pointer-events: none;
      }
    }

    ${Wrapper} {
      align-items: center;
    }
  }
`;

const StickyWrapper = styled.div`
  background-color: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.mediumGrey};
  padding: 12px 16px;
  transform: initial !important; /* Reset auto style property translateZ(0px) */
  z-index: 10;

  &.openSearch:not(.isBeingSticky) {
    border: 0;
  }

  &.isBeingSticky {
    position: sticky;
    top: 0;
    box-shadow: 0px 2px 2px -1px ${theme.colors.grey};
    border-bottom: 0;
    transition: border-top 0.4s ease-in-out, box-shadow 0.4s ease-in-out;
  }

  ${media.laptop} {
    padding: initial;
  }
`;

const IconsWrapper = styled.div`
  display: flex;
  flex-basis: 0;
  flex-grow: 1;

  &:nth-of-type(2) {
    justify-content: center;
  }
  &:last-of-type {
    justify-content: flex-end;
  }

  ${media.laptop} {
    flex: initial;

    button {
      padding: ${theme.margins.marginMedium} ${theme.margins.marginBase};
    }

    &:last-of-type {
    }
  }
`;

const IconsWrapperLogo = styled(IconsWrapper)`
  a {
    display: flex;
  }
  ${media.laptop} {
    max-width: 25%;
  }
`;

const SearchOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;

  ${media.laptop} {
    z-index: 1;
  }
`;

const LoginOverlay = styled(SearchOverlay)``;
const RecapOverlay = styled(SearchOverlay)``;

const DeactivableIcon = styled(Icon)`
  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }
`;

const StyledCartIcon = styled(DeactivableIcon)`
  margin-left: 8px;
  position: relative;

  &.hasProduct .icon-panier-noir:after {
    content: "${(props: IProductsCount): string => props.productsCount || ""}";
    position: absolute;
    top: 60%;
    left: 50%;
    transform: translate(-50%, -46%);
    color: #fff;
    font-size: 10px;
    font-family: ${theme.fonts.regular};
    font-style: normal;
  }

  ${media.laptop} {
    color: #fff;

    &.hasProduct .icon-panier-noir:after {
      content: initial;
    }
  }
`;

const SearchBar = styled.section`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  padding: 0 ${theme.margins.paddingPage};
  color: ${theme.colors.grey};
  font-size: ${theme.fontSizes.small};
  margin-top: 12px;
  background-color: ${theme.colors.white};

  ${media.laptop} {
    margin: 0 0 0 ${theme.margins.paddingPageXBig};
    flex: 1;
    position: relative;
    background-color: transparent;
    padding: 0;
  }
`;

const SearchForm = styled.form`
  display: flex;
  flex: 1;
`;

const SearchInput = styled.input`
  font-size: ${theme.fontSizes.tiny};
  border: 0;
  border-bottom: 1px solid ${theme.colors.grey};
  border-radius: 0;
  outline: 0;
  padding: 7px 0;
  flex: 1;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-appearance: none;

  &::-ms-clear {
    visibility: hidden;
  }
  &::-webkit-search-cancel-button {
    visibility: hidden;
  }

  ${media.laptop} {
    text-align: left;
    padding: ${theme.margins.paddingMedium};
  }
`;

const CloseButton = styled(Icon)`
  position: absolute;
  right: ${theme.margins.marginBase};
`;

const HeaderTablet = styled.div`
  ${Wrapper} {
    position: relative;
  }

  &.darkBackground {
    position: relative;
    z-index: 2;
    background-color: ${theme.colors.darkBlack};
    color: ${theme.colors.grey};

    i:before {
      color: ${theme.colors.grey};
    }
  }
`;

const ImageFluid = styled.img`
  ${media.laptop} {
    max-width: 100%;
  }
`;

const TopHeaderWrapper = styled(Wrapper)`
  justify-content: flex-end;
  z-index: 10;
  cursor: pointer;
`;

const TopHeaderLink = styled(Hyperlink)`
  ${media.laptop} {
    padding: ${theme.margins.paddingMedium};
    text-transform: uppercase;
    font-size: ${theme.fontSizes.tiny};
    color: ${theme.colors.white};

    &:hover {
      opacity: 0.7;
    }
  }
`;

interface ILink {
  to: string;
  text: string;
  lang: {
    fr: boolean;
    en: boolean;
  };
}
const linksList = [
  {
    lang: { fr: true, en: true },
    text: "menu.storeLocator",
    to: "links.storeLocator",
  },
  {
    to: "links.home",
    text: "menu.eshop",
    lang: { fr: false, en: true },
  },
  {
    to: "links.stores",
    text: "menu.stores",
    lang: { fr: true, en: true },
  },
  {
    to: "links.academy",
    text: "menu.academy",
    lang: { fr: true, en: false },
  },
  {
    to: "links.gift",
    text: "menu.gift",
    lang: { fr: true, en: false },
  },
  {
    to: "links.blog",
    text: "menu.blog",
    lang: { fr: true, en: false },
  },
  {
    lang: { fr: true, en: true },
    text: "menu.corporate",
    to: "links.corporate",
  },
  { to: "links.pro", text: "menu.pro", lang: { fr: true, en: false } },
];

export interface IProps extends RouteComponentProps, IWithCart {
  brandId?: string;
  productId?: string;
  productSku?: string;
  pageType?: string;
  isSticky?: boolean;
  header: IHeaderState;
  setHeader: (payload: IHeaderPayload) => ISetHeader;
  className?: string;
  noUnderMenu?: boolean;
  renderOnProps: IRenderOn;
}

type IDefaultProps = Required<Pick<IProps, "isSticky">>;

interface IState {
  isAppMenuOpen: boolean;
  isLoginOpen: boolean;
  isRecapOpen: boolean;
  isSearchOpen: boolean;
  previousIsSticky: boolean;
  searchValue: string;
  showInstantSearch: boolean;
  waitUserInput: number | null;
}

class Header extends PureComponent<IProps, IState, IDefaultProps> {
  public static defaultProps: IDefaultProps = {
    isSticky: false,
  };

  public searchbarRef: RefObject<HTMLElement> = React.createRef<HTMLElement>();
  public headerRef: RefObject<HTMLDivElement> = React.createRef<
    HTMLDivElement
  >();
  public stickyRef: RefObject<Sticky> = React.createRef<Sticky>();
  public textInput: RefObject<HTMLInputElement> = React.createRef<
    HTMLInputElement
  >();

  public state: IState = {
    isAppMenuOpen: false,
    isLoginOpen: false,
    isRecapOpen: false,
    isSearchOpen: false,
    previousIsSticky: false,
    searchValue: "",
    showInstantSearch: false,
    waitUserInput: null,
  };

  private mounted: boolean = false;

  public handleSubmitSearch = (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    event.preventDefault();
    const { current = null } = this.textInput || {};

    if (this.mounted) {
      if (this.state.waitUserInput !== null) {
        clearTimeout(this.state.waitUserInput);
      }
      this.setState({ showInstantSearch: false });
      this.toggleSearch();
    }
    if (current && current.value !== undefined) {
      const query = current.value;
      const to = `/catalogsearch/result?q=${query}`;
      if (needReload(to)) {
        window.location.assign(to);
      } else {
        this.props.history.push(to);
      }
      current.blur();
      window.scrollTo(0, 0);
    }
  };

  public setHeaderHeight = (): void => {
    const elem =
      this.headerRef.current || (this.stickyRef.current || {}).content;

    if (
      elem &&
      this.props.header &&
      elem.offsetHeight !== this.props.header.height
    ) {
      this.props.setHeader({ height: elem.offsetHeight || 0 });
    }
  };

  public setHeaderTopOffset = (): void => {
    const { header, setHeader } = this.props;
    let topVisibleOffset = header?.topVisibleOffset || 0;
    let topOffset = header?.topOffset || 0;
    const elem =
      this.headerRef.current || (this.stickyRef.current || {}).content;
    if (elem) {
      const docTop = Math.abs(document.body?.getBoundingClientRect()?.top || 0);
      const { top } = elem.getBoundingClientRect();

      if (top >= 0) {
        topVisibleOffset = Math.round(top);
      }
      if (top > 0) {
        topOffset = Math.round(top + docTop);
      }
    }

    if (elem && topOffset > 0 && topOffset !== header?.topOffset) {
      setHeader({
        topOffset,
        topVisibleOffset,
      });
    } else if (topVisibleOffset !== header?.topVisibleOffset) {
      setHeader({ topVisibleOffset });
    }
  };

  public componentDidMount = (): void => {
    this.mounted = true;
    this.props.setHeader({ isSticky: false });
    this.setHeaderHeight();
    this.setHeaderTopOffset();
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
    this.props.setHeader({ isSticky: false });
  };

  public componentDidUpdate = (prevProps: IProps, prevState: IState): void => {
    const { isSearchOpen, waitUserInput } = this.state;

    this.setHeaderHeight();
    this.setHeaderTopOffset();

    if (!prevState.isSearchOpen && this.state.isSearchOpen && this.mounted) {
      if (waitUserInput !== null) {
        clearTimeout(waitUserInput);
      }
      this.setState({
        searchValue: "",
        showInstantSearch: false,
      });
    }
    if (!this.props.isSticky && isSearchOpen) {
      window.scrollTo(0, 0);
    }
  };

  public hasProduct = (): boolean => (this.props.cart?.items.length || 0) > 0;

  public productsCount = (): number | "" => {
    const reducer = (acc: number, curr: ICartProductDetail): number =>
      acc + curr.quantity;

    const result = this.props.cart?.items.reduce(reducer, 0) || 0;

    return result === 0 || result > 99 ? "" : result;
  };

  public render(): ReactNode {
    const {
      cart,
      cartLoading,
      header,
      renderOnProps,
      renderOnProps: { mdOrDown = false, lgOrUp = false } = {},
    } = this.props;
    const {
      isAppMenuOpen,
      isLoginOpen,
      isRecapOpen,
      isSearchOpen,
      previousIsSticky,
      showInstantSearch,
    } = this.state;

    return (
      <>
        {(isSearchOpen || showInstantSearch) && (
          <SearchOverlay onClick={this.toggleSearch} />
        )}
        {isLoginOpen && <LoginOverlay onClick={this.toggleLogin} />}
        {isRecapOpen && <RecapOverlay onClick={this.toggleRecap} />}

        <Suspense fallback={<div />}>
          <AppMenu isOpen={isAppMenuOpen} toggleMenu={this.toggleAppMenu} />
        </Suspense>

        <HeaderWrapper isSearchOpen={this.state.isSearchOpen}>
          {lgOrUp &&
            this.RenderDesktopBlackHeader({
              cart,
              cartLoading,
              header,
              isSearchOpen,
              isRecapOpen,
              isLoginOpen,
              renderOnProps,
            })}
          {this.props.isSticky ? (
            <Sticky topOffset={header.topOffset || 1} ref={this.stickyRef}>
              {({
                style,
                isSticky,
              }: {
                isSticky: boolean;
                style: {};
              }): ReactNode => {
                if (previousIsSticky !== isSticky) {
                  setImmediate(() => {
                    if (this.mounted) {
                      this.props.setHeader({ isSticky });
                      this.setState({
                        previousIsSticky: isSticky,
                      });
                    }
                  });
                }

                const ret = this.renderHeader({
                  cart,
                  cartLoading,
                  header,
                  isLoginOpen,
                  isRecapOpen,
                  isSearchOpen,
                  isSticky,
                  style,
                  renderOnProps,
                });

                return ret;
              }}
            </Sticky>
          ) : (
            this.renderHeader({
              cart,
              cartLoading,
              header,
              isLoginOpen,
              isRecapOpen,
              isSearchOpen,
              isSticky: false,
              renderOnProps,
            })
          )}
        </HeaderWrapper>

        {mdOrDown && (
          <Suspense fallback={<div />}>
            <OrderRecapBody
              cart={cart}
              cartLoading={cartLoading}
              isCheckout={false}
              headerHeight={
                header
                  ? header.height +
                    (header.isSticky ? 0 : header.topVisibleOffset)
                  : 100
              }
              isOpen={isRecapOpen}
              toggleOpen={this.toggleRecap}
            />
          </Suspense>
        )}
      </>
    );
  }

  public renderHeader = ({
    isSticky,
    isLoginOpen,
    isRecapOpen,
    isSearchOpen,
    style,
    renderOnProps,
    renderOnProps: { mdOrDown, lgOrUp },
  }: {
    cart: ICart | null;
    cartLoading: boolean;
    header: IHeaderState;
    isLoginOpen: boolean;
    isRecapOpen: boolean;
    isSearchOpen: boolean;
    isSticky: boolean;
    style?: {};
    renderOnProps: IRenderOn;
  }): ReactNode => {
    return (
      <StickyWrapper
        ref={this.headerRef}
        style={style}
        className={classname({
          instantSearch: this.state.showInstantSearch,
          isBeingSticky: isSticky,
          loginOpen: isLoginOpen,
          openSearch: isSearchOpen,
          recapOpen: isRecapOpen,
        })}
        onClick={(): void => {
          if (lgOrUp) {
            if (this.state.showInstantSearch && this.mounted) {
              this.setState({ showInstantSearch: false });
            }
            if (isLoginOpen) {
              this.toggleLogin();
            }
            if (isRecapOpen) {
              this.toggleRecap();
            }
          }
        }}
      >
        {mdOrDown ? (
          <>
            <Wrapper
              onClick={(): void => {
                if (isSearchOpen) {
                  this.toggleSearch();
                }
              }}
            >
              <IconsWrapper>
                <DeactivableIcon
                  name="menu"
                  color={theme.colors.black}
                  size={24}
                  className={classname({
                    disabled: isRecapOpen || isSearchOpen,
                  })}
                  onClick={this.openAppMenu}
                  disabled={isRecapOpen || isSearchOpen}
                />
              </IconsWrapper>
              <IconsWrapperLogo>
                <Hyperlink to={translate("links.home")}>
                  <ImageFluid alt="logo LMDW" src={logoWebsite} />
                </Hyperlink>
              </IconsWrapperLogo>
              <IconsWrapper>
                <DeactivableIcon
                  name="loupe"
                  color={theme.colors.black}
                  size={23}
                  className={classname({
                    disabled: isRecapOpen,
                  })}
                  onClick={this.toggleSearch}
                  disabled={isRecapOpen}
                />
                <StyledCartIcon
                  name={
                    this.state.isRecapOpen
                      ? "cross"
                      : this.hasProduct()
                      ? "panier-noir"
                      : "panier"
                  }
                  productsCount={this.productsCount() + ""}
                  color={theme.colors.black}
                  size={24}
                  className={classname({
                    disabled: isSearchOpen,
                    hasProduct: this.hasProduct(),
                  })}
                  onClick={this.toggleRecap}
                  disabled={isSearchOpen}
                />
              </IconsWrapper>
            </Wrapper>
            {this.RenderSearchOpen({ isSearchOpen, renderOnProps })}
          </>
        ) : (
          <HeaderTablet className="container">
            <AppContentMenuList closeMenu={(): null => null} />
          </HeaderTablet>
        )}
      </StickyWrapper>
    );
  };

  public RenderSearchOpen = ({
    isSearchOpen,
    renderOnProps: { mdOrDown = true },
  }: {
    isSearchOpen: boolean;
    renderOnProps: IRenderOn;
  }): ReactNode => {
    const searchbarContainer =
      document.querySelector("#searchbar") || document.body;

    const searchbar = this.searchbarRef.current;
    const instantSearchTop = searchbar && searchbar.offsetHeight;

    // Instanciate SearchBar on desktop and on mobile if isSearchOpen is true
    return (mdOrDown && isSearchOpen) || !mdOrDown ? (
      <SearchBar id="searchbar" ref={this.searchbarRef}>
        <SearchForm action="#" onSubmit={this.handleSubmitSearch}>
          <SearchInput
            type="search"
            placeholder={translate("searchBar.placeholder")}
            ref={this.textInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              if (this.state.waitUserInput) {
                clearTimeout(this.state.waitUserInput);
              }
              const searchValue = e.currentTarget.value;
              if (this.mounted) {
                this.setState({
                  waitUserInput: setTimeout(() => {
                    if (this.mounted) {
                      this.setState({
                        searchValue,
                        showInstantSearch: searchValue !== "",
                        waitUserInput: null,
                      });
                    }
                  }, 250),
                });
              }
            }}
            autoFocus
          />
          <input type="submit" style={{ display: "none" }} />
        </SearchForm>

        {mdOrDown && (
          <CloseButton
            onClick={this.toggleSearch}
            name="cross"
            size={24}
            color={theme.colors.grey}
          />
        )}

        {ReactDOM.createPortal(
          <Suspense fallback={<div />}>
            <InstantSearch
              show={this.state.showInstantSearch}
              hide={(): void => {
                if (this.mounted) {
                  this.setState({ showInstantSearch: false });
                }
              }}
              input={this.state.searchValue}
              className={classname(
                "instant-search",
                this.state.showInstantSearch && "instant-search-open"
              )}
              positionTop={instantSearchTop}
            />
          </Suspense>,
          searchbarContainer
        )}
      </SearchBar>
    ) : null;
  };

  public RenderDesktopBlackHeader = ({
    cart,
    cartLoading,
    header,
    isLoginOpen,
    isRecapOpen,
    isSearchOpen,
    renderOnProps,
  }: {
    cart: ICart | null;
    cartLoading: boolean;
    header: IHeaderState;
    isLoginOpen: boolean;
    isRecapOpen: boolean;
    isSearchOpen: boolean;
    renderOnProps: IRenderOn;
  }): JSX.Element => {
    const currentLanguage = i18next.language;

    return (
      <HeaderTablet className="darkBackground">
        <TopHeaderWrapper className="container">
          {linksList.map(
            (link: ILink, index: number): ReactNode =>
              link.lang[currentLanguage] && (
                <TopHeaderLink to={translate(link.to)} key={index}>
                  {translate(link.text)}
                </TopHeaderLink>
              )
          )}
          <LangPicker />
        </TopHeaderWrapper>
        <Wrapper
          onClick={(): void => {
            if (isSearchOpen) {
              this.toggleSearch();
            }
          }}
          className="container"
        >
          <IconsWrapperLogo>
            <Hyperlink to={translate("links.home")}>
              <ImageFluid alt="logo LMDW" src={logoWebsiteDesktop} />
            </Hyperlink>
          </IconsWrapperLogo>
          {this.RenderSearchOpen({ isSearchOpen, renderOnProps })}
          <IconsWrapper>
            <Icon
              name="loupe"
              color={theme.colors.black}
              size={23}
              className={classname({
                disabled: isRecapOpen || isLoginOpen,
              })}
              onClick={this.handleSubmitSearch}
              disabled={isRecapOpen || isLoginOpen}
            />
            <MiniLogin
              toggleLogin={this.toggleLogin}
              isLoginOpen={isLoginOpen}
              className={classname({
                disabled: isRecapOpen || this.state.showInstantSearch,
              })}
            />
            <MiniCart
              hasProduct={this.hasProduct()}
              productsCount={this.productsCount()}
              isSearchOpen={isSearchOpen}
              toggleRecap={this.toggleRecap}
              cart={cart}
              cartLoading={cartLoading}
              isCheckout={false}
              isRecapOpen={isRecapOpen}
              headerHeight={
                header
                  ? header.height +
                    (header.isSticky ? 0 : header.topVisibleOffset)
                  : 100
              }
              className={classname({
                disabled: isLoginOpen || this.state.showInstantSearch,
              })}
            />
          </IconsWrapper>
        </Wrapper>
      </HeaderTablet>
    );
  };

  public openAppMenu = (): void => {
    if (this.mounted) {
      this.setState({ isAppMenuOpen: true });
    }
  };

  public toggleAppMenu = (state: State): void => {
    if (this.mounted) {
      this.setState({
        isAppMenuOpen: state.isOpen,
      });
    }
  };

  public toggleLogin = (e?: React.MouseEvent): void => {
    if (e) {
      e.stopPropagation();
    }

    if (this.mounted) {
      this.setState((prevState: IState) => ({
        isLoginOpen: !prevState.isLoginOpen,
      }));
    }
  };

  public toggleRecap = (e?: React.MouseEvent): void => {
    if (e) {
      e.stopPropagation();
    }

    this.setHeaderHeight();
    if (this.mounted) {
      this.setState((prevState: IState) => ({
        isRecapOpen: !prevState.isRecapOpen,
      }));
    }
  };

  public toggleSearch = (): void => {
    if (this.mounted) {
      this.setState((prevState: IState) => ({
        isSearchOpen: !prevState.isSearchOpen,
        searchValue: prevState.isSearchOpen ? "" : prevState.searchValue,
      }));
    }
  };
}

export default withRenderOn(Header);
